/**
 * 3D Room Visualizer — the yard (calculations/plan/yard.ts) as meshes: the
 * lawn, the concrete slabs (a driveway outside each garage door, a landing
 * outside each door), and a board fence round the lawn's edge, in the focus
 * room's coordinates.
 *
 * The group and every mesh carry `userData.yard`. The yard is for the RENDER
 * only: the viewer keeps it hidden, leaves it out when it fits the camera to
 * the room, and shows it just while the export is taken (Room3DViewer
 * exportScene), where the meshes are tagged by their materials' kind
 * (`slab`, `lawn`, `fence`) for the renderer's own finishes.
 *
 * Heights: the slabs lie SLAB_Y, a hair under the floor — they run from the
 * wall's inside face (the threshold) out under the framing, where a plan's
 * floor pad also reaches, and the floor must win there; the lawn lies LAWN_Y
 * below them — and above the renderer's view-outside disc (render_room.py
 * VIEW_DISC_Z, −0.005 m), which it must stay over.
 *
 * The fence is one mesh of boxes (posts, rails, a panel a section): its UVs
 * run in FEET — u along the fence, v up — so the renderer can cut the
 * pickets' grooves from them whichever way the fence runs.
 */

import * as THREE from 'three';
import { Yard, YardRect } from '../../../calculations/plan/yard';
import { createFenceMaterial, createLawnMaterial, createSlabMaterial } from './materialFactory';

/** The slabs' height (feet): a hair under the floor, which runs over their first inches. */
export const SLAB_Y = -0.004;
/** The lawn's height (feet): under the slabs, over the renderer's view disc. */
export const LAWN_Y = -0.01;
/** The textures tile every this many feet. */
const SLAB_TILE_FT = 10;
const LAWN_TILE_FT = 4;

// The fence: a 5 ft board fence on 4x4 posts 8 ft apart, two 2x4 rails, boards an inch thick.
export const FENCE_SECTION_FT = 8;
export const FENCE_HEIGHT_FT = 5;
const POST_FT = 3.5 / 12;
const RAIL_H_FT = 3.5 / 12;
const RAIL_T_FT = 1.5 / 12;
const BOARD_T_FT = 1 / 12;
const BOARD_BOTTOM_FT = 0.2;
/** A section the driveway crosses (and this much either side of it) has no boards or rails: the way out. */
const GATE_CLEAR_FT = 2;

function plane(r: YardRect, y: number, material: THREE.Material, tileFt: number): THREE.Mesh | null {
  const w = r.x1 - r.x0, d = r.z1 - r.z0;
  if (w <= 0.01 || d <= 0.01) return null;
  const geo = new THREE.PlaneGeometry(w, d);
  // The texture tiles by the world size, not once per plane (the textures are shared, so the UVs carry the scale).
  const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (w / tileFt), uv.getY(i) * (d / tileFt));
  uv.needsUpdate = true;
  const mesh = new THREE.Mesh(geo, material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set((r.x0 + r.x1) / 2, y, (r.z0 + r.z1) / 2);
  mesh.receiveShadow = true;
  mesh.castShadow = false;
  mesh.userData.yard = true;
  return mesh;
}

/** An axis-aligned box, feet; `along` says which axis the fence it belongs to runs on (its u). */
interface Box { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number; along: 'x' | 'z' }

/**
 * Boxes as one geometry (positions, normals, UVs), without three's example
 * modules: u = feet along the fence, v = feet up, on every face.
 */
export function boxesGeometry(boxes: Box[]): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const quad = (p: [number, number, number][], n: [number, number, number], along: 'x' | 'z') => {
    const tri = (a: number, b: number, c: number) => {
      for (const i of [a, b, c]) {
        positions.push(...p[i]);
        normals.push(...n);
        uvs.push(along === 'x' ? p[i][0] : p[i][2], p[i][1]);
      }
    };
    tri(0, 1, 2);
    tri(0, 2, 3);
  };
  for (const b of boxes) {
    const { x0, x1, y0, y1, z0, z1, along } = b;
    quad([[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [0, 0, 1], along);     // +z
    quad([[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]], [0, 0, -1], along);    // -z
    quad([[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], [1, 0, 0], along);     // +x
    quad([[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], [-1, 0, 0], along);    // -x
    quad([[x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0]], [0, 1, 0], along);     // top
    quad([[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], [0, -1, 0], along);    // bottom
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  return geo;
}

/** The spans along each edge of the lawn where a slab reaches that edge (the driveway's way out). */
function slabSpansAt(yard: Yard): Record<'front' | 'back' | 'left' | 'right', [number, number][]> {
  const { lawn } = yard;
  const near = (a: number, b: number) => Math.abs(a - b) < 0.01;
  const out: Record<'front' | 'back' | 'left' | 'right', [number, number][]> = { front: [], back: [], left: [], right: [] };
  for (const s of yard.slabs) {
    if (near(s.z0, lawn.z0)) out.front.push([s.x0, s.x1]);
    if (near(s.z1, lawn.z1)) out.back.push([s.x0, s.x1]);
    if (near(s.x0, lawn.x0)) out.left.push([s.z0, s.z1]);
    if (near(s.x1, lawn.x1)) out.right.push([s.z0, s.z1]);
  }
  return out;
}

/** The fence's boxes round the lawn: posts every section, and the rails and a board panel of each section no driveway crosses. */
export function fenceBoxes(yard: Yard): Box[] {
  const { lawn } = yard;
  const gaps = slabSpansAt(yard);
  const boxes: Box[] = [];
  // Each edge: the line it runs along (a from..to span on `along`), its fixed coordinate across, and the gaps in it.
  const edges: { along: 'x' | 'z'; from: number; to: number; at: number; gaps: [number, number][] }[] = [
    { along: 'x', from: lawn.x0, to: lawn.x1, at: lawn.z0, gaps: gaps.front },
    { along: 'x', from: lawn.x0, to: lawn.x1, at: lawn.z1, gaps: gaps.back },
    { along: 'z', from: lawn.z0, to: lawn.z1, at: lawn.x0, gaps: gaps.left },
    { along: 'z', from: lawn.z0, to: lawn.z1, at: lawn.x1, gaps: gaps.right },
  ];
  const box = (along: 'x' | 'z', a0: number, a1: number, y0: number, y1: number, c0: number, c1: number): Box => (along === 'x'
    ? { x0: a0, x1: a1, y0, y1, z0: c0, z1: c1, along }
    : { x0: c0, x1: c1, y0, y1, z0: a0, z1: a1, along });
  for (const e of edges) {
    const length = e.to - e.from;
    if (length < FENCE_SECTION_FT) continue;
    const sections = Math.max(1, Math.round(length / FENCE_SECTION_FT));
    const step = length / sections;
    // The fence's centre line sits half a post inside the lawn's edge, so it is all on the lawn.
    const centre = e.at + (e.at <= (e.along === 'x' ? (lawn.z0 + lawn.z1) / 2 : (lawn.x0 + lawn.x1) / 2) ? POST_FT / 2 : -POST_FT / 2);
    // A section the driveway crosses (and GATE_CLEAR_FT either side) is the way out: no boards, no rails, and no post inside it.
    const open = (a0: number, a1: number) => e.gaps.some(([g0, g1]) => a0 < g1 + GATE_CLEAR_FT && a1 > g0 - GATE_CLEAR_FT);
    for (let i = 0; i <= sections; i++) {
      const a = e.from + i * step;
      const before = i > 0 && open(e.from + (i - 1) * step, a), after = i < sections && open(a, e.from + (i + 1) * step);
      if (before && after) continue; // a post between two open sections would stand in the driveway
      boxes.push(box(e.along, a - POST_FT / 2, a + POST_FT / 2, 0, FENCE_HEIGHT_FT + 0.2, centre - POST_FT / 2, centre + POST_FT / 2));
    }
    for (let i = 0; i < sections; i++) {
      const a0 = e.from + i * step + POST_FT / 2, a1 = e.from + (i + 1) * step - POST_FT / 2;
      if (open(a0, a1)) continue;
      for (const railY of [1.2, FENCE_HEIGHT_FT - 0.8]) boxes.push(box(e.along, a0, a1, railY, railY + RAIL_H_FT, centre - RAIL_T_FT / 2, centre + RAIL_T_FT / 2));
      // The boards, as one panel, on the outside face of the rails.
      const outside = centre + (centre > e.at ? -1 : 1) * (RAIL_T_FT / 2 + BOARD_T_FT / 2);
      boxes.push(box(e.along, a0, a1, BOARD_BOTTOM_FT, FENCE_HEIGHT_FT, outside - BOARD_T_FT / 2, outside + BOARD_T_FT / 2));
    }
  }
  return boxes;
}

/** The yard as a group named `yard`, in the coordinates the yard was given in (the focus room's). */
export function buildYard(yard: Yard): THREE.Group {
  const group = new THREE.Group();
  group.name = 'yard';
  group.userData.yard = true;
  const lawn = plane(yard.lawn, LAWN_Y, createLawnMaterial(), LAWN_TILE_FT);
  if (lawn) { lawn.name = 'lawn'; group.add(lawn); }
  const slabMaterial = createSlabMaterial();
  for (const s of yard.slabs) {
    const mesh = plane(s, SLAB_Y, slabMaterial, SLAB_TILE_FT);
    if (mesh) { mesh.name = `slab-${s.kind}`; group.add(mesh); }
  }
  const boxes = fenceBoxes(yard);
  if (boxes.length) {
    const fence = new THREE.Mesh(boxesGeometry(boxes), createFenceMaterial());
    fence.name = 'fence';
    fence.castShadow = true;
    fence.receiveShadow = true;
    fence.userData.yard = true;
    group.add(fence);
  }
  return group;
}
